import { useEffect } from "react";
import {
  Button,
  CONCEPT_ICONS,
  PocketRow,
  PocketSection,
  PocketStatement,
} from "@/components/ui";
import { useT } from "@/i18n";
import { settingsConnectionsMessages } from "@/i18n/messages/settings-connections";
import {
  COMPOSIO_UI_ENABLED,
  connectionStatusLabel,
  toolkitDisplayName,
} from "@/lib/composio";
import { browserConnectionApplies } from "@/desktop/browser-connection";
import { browserExtensionApplies } from "@/desktop/browser-extension-setup";
import { BrowserConnectionsSection } from "./browser-connections-section";
import { selectToolkit, useIntegrationStore } from "@/stores/integration-store";
import { useAppAttentionCount } from "./connections-state";
import { useSettingsNav } from "./settings-navigation";

export function ConnectionsPage() {
  const t = useT(settingsConnectionsMessages);
  const nav = useSettingsNav();
  const connections = useIntegrationStore((state) => state.connections);
  const pending = useIntegrationStore((state) => state.pendingAuth);
  const toolkits = useIntegrationStore((state) => state.toolkits);
  const resolvedToolkits = useIntegrationStore(
    (state) => state.resolvedToolkits,
  );
  const actions = useIntegrationStore((state) => state.actions);
  const error = useIntegrationStore((state) => state.error);
  const count = useAppAttentionCount();
  const browsersApply = browserConnectionApplies() || browserExtensionApplies();
  useEffect(() => {
    if (COMPOSIO_UI_ENABLED) void actions.refreshConnections();
  }, [actions]);
  const slugs = COMPOSIO_UI_ENABLED
    ? [...new Set([...connections.keys(), ...pending.keys()])].filter(
        (slug) =>
          slug !== "composio" &&
          (pending.has(slug) || connections.get(slug)?.status !== "inactive"),
      )
    : [];
  const slugKey = slugs.join(",");
  useEffect(() => {
    for (const slug of slugKey.split(",").filter(Boolean))
      void actions.resolveToolkit(slug);
  }, [actions, slugKey]);
  const activeApps = COMPOSIO_UI_ENABLED
    ? [...connections.values()].filter(
        (connection) =>
          connection.status === "active" &&
          connection.toolkit_slug !== "composio",
      ).length
    : 0;
  return (
    <div data-testid="settings-page-connections">
      <PocketStatement
        size="panel"
        title={
          count
            ? t("pageTitleNeedsYou", { count })
            : browsersApply
              ? t("pageTitleYours")
              : t("pageTitleAllConnected")
        }
        line={t("pageLineApps", { count: activeApps })}
      />
      {COMPOSIO_UI_ENABLED && (
        <PocketSection title={t("appsTitle")}>
          {error && (
            <p role="alert" className="px-2 text-appkit-small text-danger">
              {error}
            </p>
          )}
          {slugs.map((slug) => {
            const connection = connections.get(slug);
            const name = toolkitDisplayName(
              slug,
              selectToolkit({ toolkits, resolvedToolkits }, slug),
            );
            const status = pending.has(slug) ? "initiated" : connection?.status;
            return (
              <PocketRow
                key={slug}
                testId={`connections-app-${slug}`}
                title={name}
                leading={<CONCEPT_ICONS.app className="h-4 w-4 text-muted" />}
                secondLine={
                  status ? connectionStatusLabel(status) : t("connecting")
                }
                status={
                  status === "expired" || status === "failed"
                    ? { tone: status === "failed" ? "error" : "question" }
                    : undefined
                }
                chevron
                onSelect={() =>
                  nav.push({
                    page: "connections-app",
                    args: { app: slug, title: name },
                  })
                }
                trailing={
                  status === "expired" || status === "failed" ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`connections-reconnect-${slug}`}
                      onClick={() => void actions.initiateConnection(slug)}
                    >
                      {t("reconnect")}
                    </Button>
                  ) : undefined
                }
              />
            );
          })}
          <PocketRow
            testId="connections-add-app"
            title={t("addApp")}
            leading={<CONCEPT_ICONS.app className="h-4 w-4 text-muted" />}
            chevron
            onSelect={() => nav.push({ page: "connections-add-app" })}
          />
        </PocketSection>
      )}
      <BrowserConnectionsSection />
    </div>
  );
}
